/**
 * Zodiac Wheel Component
 * Positions 12 zodiac signs around an elliptical wheel
 * Works across all device sizes and aspect ratios
 */
class ZodiacWheel {
  constructor() {
    this.wheel = document.querySelector('.zodiac-wheel__circle');
    this.signs = document.querySelectorAll('.zodiac-wheel__sign');
    this.gridItems = document.querySelectorAll('.zodiac-wheel__grid-item');
    this.resizeObserver = null;
    
    if (this.wheel && this.signs.length > 0) {
      this.init();
    }
  }

  init() {
    // Initial position calculation
    this.calculatePositions();
    
    // Setup interactions
    this.setupHoverEffects();
    this.setupScrollAnimation();
    
    // Handle window resize
    window.addEventListener('resize', () => this.debouncedCalculate());
    
    // Recalculate after fonts/images load
    if (document.readyState === 'complete') {
      this.calculatePositions();
    } else {
      window.addEventListener('load', () => this.calculatePositions());
    }
    
    // Handle orientation change (important for foldable devices)
    window.addEventListener('orientationchange', () => {
      setTimeout(() => this.calculatePositions(), 100);
    });
    
    // Use ResizeObserver for more accurate container size detection
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver((entries) => {
        // Use requestAnimationFrame to avoid layout thrashing
        requestAnimationFrame(() => this.calculatePositions());
      });
      this.resizeObserver.observe(this.wheel);
    }
    
    // Final recalculation after a short delay to ensure proper layout
    setTimeout(() => this.calculatePositions(), 100);
    setTimeout(() => this.calculatePositions(), 500);
  }

  /**
   * Debounced calculate to prevent excessive recalculations during resize
   */
  debouncedCalculate() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => this.calculatePositions(), 50);
  }

  /**
   * Calculate and set positions for all zodiac signs
   * Uses true geometric center with dynamic padding for perfect alignment across all devices
   */
  calculatePositions() {
    if (!this.wheel || this.signs.length === 0) return;

    // Use rendered size (sub-pixel accurate) for consistent math on tablets/foldables.
    const wheelRect = this.wheel.getBoundingClientRect();
    const width = wheelRect.width;
    const height = wheelRect.height;
    if (!width || !height) return;
    
    // TRUE CENTER
    const centerX = width / 2;
    const centerY = height / 2;

    // Measure the largest rendered sign to avoid overflow on variable text/layout engines.
    let maxSignWidth = 0;
    let maxSignHeight = 0;
    this.signs.forEach((sign) => {
      const rect = sign.getBoundingClientRect();
      maxSignWidth = Math.max(maxSignWidth, rect.width || sign.offsetWidth || 0);
      maxSignHeight = Math.max(maxSignHeight, rect.height || sign.offsetHeight || 0);
    });

    const signHalfWidth = (maxSignWidth || 100) / 2;
    const signHalfHeight = (maxSignHeight || 100) / 2;

    // Geometry-based edge padding scales with wheel size (no hardcoded device buckets).
    const minDimension = Math.min(width, height);
    const edgePadding = Math.max(6, minDimension * 0.035);

    // Radii for sign centers. Keep a small minimum for very constrained containers.
    let radiusX = centerX - signHalfWidth - edgePadding;
    let radiusY = centerY - signHalfHeight - edgePadding;
    const minRadius = minDimension * 0.12;
    radiusX = Math.max(radiusX, minRadius);
    radiusY = Math.max(radiusY, minRadius);

    // Position each sign around the ellipse
    this.signs.forEach((sign, index) => {
      // Each sign is 30 degrees apart (360 / 12 = 30)
      const angleDeg = index * 30;
      // Convert to radians, offset by -90 so first sign is at top (12 o'clock)
      const angleRad = (angleDeg - 90) * (Math.PI / 180);

      // Calculate position on ellipse
      const x = centerX + radiusX * Math.cos(angleRad);
      const y = centerY + radiusY * Math.sin(angleRad);

      // Neutralize legacy negative margins so translate(-50%, -50%) is the single centering source.
      sign.style.marginLeft = '0px';
      sign.style.marginTop = '0px';

      // Apply position - transform: translate(-50%, -50%) centers the sign on the point
      sign.style.left = `${x}px`;
      sign.style.top = `${y}px`;
      sign.style.transform = 'translate(-50%, -50%)';
    });
    
    // Debug logging (uncomment to troubleshoot on specific devices)
    // console.log(`Viewport: ${window.innerWidth}px | Wheel: ${width.toFixed(1)}x${height.toFixed(1)} | Center: (${centerX.toFixed(1)}, ${centerY.toFixed(1)}) | Radii: (${radiusX.toFixed(1)}, ${radiusY.toFixed(1)}) | SignMax: ${maxSignWidth.toFixed(1)}x${maxSignHeight.toFixed(1)} | EdgePad: ${edgePadding.toFixed(1)}px`);
  }

  setupHoverEffects() {
    this.signs.forEach((sign) => {
      sign.addEventListener('mouseenter', () => {
        const signName = sign.getAttribute('data-sign');
        this.highlightGridItem(signName);
      });
      
      sign.addEventListener('mouseleave', () => {
        this.removeHighlight();
      });
      
      // Add touch support for mobile
      sign.addEventListener('touchstart', (e) => {
        const signName = sign.getAttribute('data-sign');
        this.highlightGridItem(signName);
      }, { passive: true });
    });
  }

  highlightGridItem(signName) {
    this.gridItems.forEach((item) => {
      if (item.getAttribute('data-sign') === signName) {
        item.style.transform = 'scale(1.05)';
        item.style.transition = 'transform 0.3s ease';
      }
    });
  }

  removeHighlight() {
    this.gridItems.forEach((item) => {
      item.style.transform = '';
    });
  }

  setupScrollAnimation() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('visible');
            }, index * 100);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    this.gridItems.forEach((item) => {
      observer.observe(item);
    });
  }
  
  /**
   * Cleanup method for memory management
   */
  destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    window.removeEventListener('resize', this.debouncedCalculate);
    window.removeEventListener('orientationchange', this.calculatePositions);
  }
}

// Initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new ZodiacWheel());
} else {
  new ZodiacWheel();
}

// Reinitialize in theme editor
if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => new ZodiacWheel());
}
